import React, { useEffect, useState } from "react";
import DetailSheet from "../session/generate/DetailSheet";
import DraggableStepper from "../ui/draggable-stepper";
import { ChipGrid, Field, SwitchRow } from "../profile/ProfileParts";
import { coarsePosition } from "../../utils/sessionWeather";

const TITLES = {
  rests: "Rests & interruptions",
  coachScreen: "Coach screen",
  countdowns: "Countdowns",
  phone: "Phone",
  offer: "In workouts",
  badges: "Badges",
};

const FILL_DRAIN = [
  { value: false, label: "Fill up" },
  { value: true, label: "Drain" },
];

/**
 * The small sheets behind the Settings rows (issue #86). Each groups the
 * switches that answer one question (how a session ends and resumes, what the
 * Coach screen does, how countdowns sound, what the phone does), so no row on
 * the page is a lone switch.
 *
 * These are playback settings in localStorage-backed hooks with no server
 * merge-back, so every control saves as it changes (unlike Profile, which
 * saves on close).
 */
const SettingsSheet = ({ kind, onClose, workoutSettings, updateWorkoutSettings, audio, updateAudio }) => {
  // Keep the last sheet's content while it animates closed.
  const [shown, setShown] = useState(kind);
  useEffect(() => {
    if (kind) setShown(kind);
  }, [kind]);

  const ws = workoutSettings;
  const [weatherNote, setWeatherNote] = useState("");
  // Ask for location as the switch goes on, so the phone's prompt shows here
  // rather than mid-session; a refusal leaves it off.
  const setWeather = async (on) => {
    setWeatherNote("");
    if (!on) {
      updateWorkoutSettings({ weatherOnSessions: false });
      return;
    }
    try {
      await coarsePosition();
      updateWorkoutSettings({ weatherOnSessions: true });
    } catch {
      setWeatherNote("Khronos couldn't get your location. Allow it for this site in your browser, then try again.");
    }
  };
  const offerPairing = (
    <SwitchRow
      id={`offer-pairing-${shown}`}
      label="Offer pairing"
      description="Shows Connect Equipment under the ring in bike and rower blocks."
      checked={ws.showConnectTrainer ?? true}
      onCheckedChange={(v) => updateWorkoutSettings({ showConnectTrainer: v })}
    />
  );

  let body = null;
  switch (shown) {
    case "badges":
      body = (
        <SwitchRow
          id="show-badges"
          label="Show badges"
          description="Badges on the Mates tab, and a notification when you earn one. Off, they're still counted quietly, so turning this back on shows everything you've earned."
          checked={ws.showBadges ?? true}
          onCheckedChange={(v) => updateWorkoutSettings({ showBadges: v })}
        />
      );
      body = (
        <>
          {body}
          {/* The mates' weekly board (lib/board.js on the API): opt-in. */}
          {(ws.showBadges ?? true) && (
            <SwitchRow
              id="show-on-board"
              label="Show me on my mates' board"
              description="A weekly board on Activity for mates who switch this on too. It ranks how far you are towards your own target, and how your rows and rides compare with your usual, never raw speed. Resets every Monday."
              checked={ws.showOnBoard ?? false}
              onCheckedChange={(v) => updateWorkoutSettings({ showOnBoard: v })}
            />
          )}
        </>
      );
      break;
    // The changeover hold moved to Profile's Changeovers, beside the times it
    // holds between; what's left here is the end of a session and a break.
    case "rests":
      body = (
        <>
          <SwitchRow
            id="skip-last-rest"
            label="Skip the last rest"
            description="Skips the rests at the end of the final round, so the session ends when the work does."
            checked={ws.skipLastRest ?? true}
            onCheckedChange={(v) => updateWorkoutSettings({ skipLastRest: v })}
          />
          <Field
            label="Interrupted workouts"
            hint="How long Khronos waits after your last recorded data before offering to pick up a workout you left. Short app switches stay under this, so they won't interrupt you."
          >
            <DraggableStepper
              label="Offer to recover after"
              value={ws.recoveryIdleThresholdMinutes ?? 10}
              onChange={(v) => updateWorkoutSettings({ recoveryIdleThresholdMinutes: v })}
              min={1}
              max={30}
              step={1}
              unit="min"
            />
          </Field>
        </>
      );
      break;
    case "coachScreen":
      body = (
        <>
          <Field label="Phase ring" hint="The big ring that shows time left in this phase.">
            <ChipGrid
              columns={2}
              options={FILL_DRAIN}
              value={ws.drainTimer ?? false}
              onChange={(v) => updateWorkoutSettings({ drainTimer: v })}
            />
          </Field>
          <Field label="Screen" hint="Turn with phone suits a phone mounted sideways, like on a rower.">
            <ChipGrid
              columns={2}
              options={[
                { value: "auto", label: "Turn with phone" },
                { value: "portrait", label: "Always upright" },
              ]}
              value={ws.coachOrientation ?? "auto"}
              onChange={(v) => updateWorkoutSettings({ coachOrientation: v })}
            />
          </Field>
          <Field label="Segment rings" hint="The small rings that count rounds and sets.">
            <ChipGrid
              columns={2}
              options={FILL_DRAIN}
              value={ws.drainSegments ?? false}
              onChange={(v) => updateWorkoutSettings({ drainSegments: v })}
            />
          </Field>
          <Field label="While you train">
            <div className="grid gap-4 rounded-xl border border-border bg-background p-3">
              <SwitchRow
                id="auto-follow-pane"
                label="Follow the equipment"
                description="Slides to the bike or rower screen on equipment phases, and back to the Coach for strength."
                checked={ws.autoFollowPhasePane ?? true}
                onCheckedChange={(v) => updateWorkoutSettings({ autoFollowPhasePane: v })}
              />
              <SwitchRow
                id="pause-on-macro-tap"
                label="Pause to preview"
                description="Tapping a block in the stepper pauses the timer until you go back."
                checked={ws.pauseOnMacroTap ?? true}
                onCheckedChange={(v) => updateWorkoutSettings({ pauseOnMacroTap: v })}
              />
              {offerPairing}
            </div>
          </Field>
        </>
      );
      break;
    case "countdowns":
      body = (
        <>
          <Field label="Count-in" hint="A countdown before the first phase starts, so you can get into position.">
            <ChipGrid
              options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${n} s` }))}
              value={audio.countInDuration}
              onChange={(v) => updateAudio({ countInDuration: v })}
            />
          </Field>
          <SwitchRow
            id="countdown-ticks"
            label="Countdown ticks"
            description="A tick for each of the last 3 seconds of every phase."
            checked={!!audio.countdownEnabled}
            onCheckedChange={(v) => updateAudio({ countdownEnabled: v })}
          />
        </>
      );
      break;
    case "phone":
      body = (
        <>
          <SwitchRow
            id="haptic"
            label="Vibrate"
            description="On each phase change. Works on most Android phones; iPhones don't allow it from a web app."
            checked={!!audio.hapticEnabled}
            onCheckedChange={(v) => updateAudio({ hapticEnabled: v })}
          />
          <SwitchRow
            id="wake-lock"
            label="Keep screen on"
            description="While a workout is running. Uses a little more battery."
            checked={!!ws.wakeLockEnabled}
            onCheckedChange={(v) => updateWorkoutSettings({ wakeLockEnabled: v })}
          />
          <SwitchRow
            id="weather-on-sessions"
            label="Weather on sessions"
            description="Notes the outside temperature where you train, from the Open-Meteo weather service. Your rough location goes to them when a session starts and isn't saved."
            checked={!!ws.weatherOnSessions}
            onCheckedChange={setWeather}
          />
          {weatherNote && <p className="text-xs text-destructive">{weatherNote}</p>}
        </>
      );
      break;
    case "offer":
      body = (
        <>
          {offerPairing}
          <p className="text-xs text-muted-foreground">The same switch is in Coach screen.</p>
        </>
      );
      break;
    default:
      body = null;
  }

  return (
    <DetailSheet
      open={!!kind}
      onOpenChange={(o) => !o && onClose()}
      title={TITLES[shown] || ""}
    >
      {body}
    </DetailSheet>
  );
};

export default SettingsSheet;
